import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableFooter,
	TableHead,
	TableRow,
	Tooltip,
} from "@mui/material";

import { HeatMechanicalResult } from "../../models/HeatMechanicalResult";
import { MechanicalTestRunDisposition } from "../../models/MechanicalTestRunDisposition";
import {
	CommentCell,
	DispositionHistory,
	ResultChip,
	StatusChip,
	TimestampCell,
} from "./cells";
import { disposedByLabel } from "./format";
import { headerCellSx, refCellSx, VALUE_COLUMNS } from "./layout";
import { RunGroup, buildRunGroups } from "./runGroups";

// Read-only port of the mill's MechanicalResultsTable (no run editing).
type Props = {
	results: HeatMechanicalResult[];
	dispositions?: MechanicalTestRunDisposition[];
	maxHeight?: number | string;
};

export const HeatResultsTable = ({
	results,
	dispositions = [],
	maxHeight = 420,
}: Props) => {
	const groups = buildRunGroups(results, dispositions);

	const refResult = groups.flatMap((group) => group.results)[0] ?? null;
	const hasMixedLimits =
		new Set(
			results.map((result: HeatMechanicalResult) =>
				[
					result.YieldMin,
					result.YieldMax,
					result.TensileMin,
					result.TensileMax,
					result.ElongationPercentMin,
					result.PercentRAMin,
					result.PercentRAMax,
					result.Unit,
				].join("|"),
			),
		).size > 1;

	const renderRunCells = (group: RunGroup) => {
		const rowSpan = Math.max(group.results.length, 1);

		return (
			<>
				<TableCell
					rowSpan={rowSpan}
					sx={{ borderLeft: 1, borderColor: "divider" }}
					align="center"
				>
					<TimestampCell timestamp={group.reported} />
				</TableCell>
				<TableCell rowSpan={rowSpan} align="center">
					<StatusChip code={group.disposition?.Disposition} />
				</TableCell>
				<TableCell rowSpan={rowSpan} align="center">
					{disposedByLabel(group.disposition)}
				</TableCell>
				<TableCell rowSpan={rowSpan} align="center">
					<TimestampCell timestamp={group.disposition?.DisposedAt} />
				</TableCell>
				<TableCell
					rowSpan={rowSpan}
					align="center"
					sx={{ px: 0.25, width: 56, whiteSpace: "nowrap" }}
				>
					<CommentCell
						comment={group.comment}
						label={group.results[0]?.HeatName ?? null}
					/>
					<DispositionHistory history={group.history} />
				</TableCell>
			</>
		);
	};

	return (
		<TableContainer
			sx={{
				width: "100%",
				boxSizing: "border-box",
				overflowX: "auto",
				maxHeight,
				border: "1px solid #ccc",
				borderRadius: "4px",
			}}
		>
			<Table
				stickyHeader
				size="small"
				sx={{
					width: "auto",
					minWidth: "100%",
					"& .MuiTableCell-root": {
						fontSize: 14,
						px: 0.75,
						whiteSpace: "nowrap",
					},
					"& .MuiTableCell-head": {
						fontSize: 12,
					},
					"& .MuiTableCell-footer": {
						fontSize: 12,
					},
				}}
			>
				<TableHead>
					<TableRow>
						<TableCell sx={headerCellSx}>Seq</TableCell>
						{VALUE_COLUMNS.map((column) => (
							<TableCell key={column.field} sx={headerCellSx}>
								{column.label}
							</TableCell>
						))}
						<TableCell sx={headerCellSx} align="center">
							Test
						</TableCell>
						<TableCell
							sx={{ ...headerCellSx, borderLeft: 1, borderColor: "divider" }}
							align="center"
						>
							Reported At
						</TableCell>
						<TableCell sx={headerCellSx} align="center">
							Result
						</TableCell>
						<TableCell sx={headerCellSx} align="center">
							By
						</TableCell>
						<TableCell sx={headerCellSx} align="center">
							At
						</TableCell>
						<TableCell sx={{ ...headerCellSx, width: 56 }} align="center" />
					</TableRow>
				</TableHead>

				<TableBody>
					{groups.map((group: RunGroup, groupIndex: number) => {
						const rowSx = (isFirstRowOfGroup: boolean) =>
							groupIndex > 0 && isFirstRowOfGroup
								? { borderTop: 2, borderColor: "divider" }
								: {};

						if (group.results.length === 0)
							return (
								<TableRow key={group.runId} sx={rowSx(true)}>
									<TableCell
										colSpan={VALUE_COLUMNS.length + 2}
										sx={{ color: "text.secondary" }}
									>
										No specimens loaded
									</TableCell>
									{renderRunCells(group)}
								</TableRow>
							);

						return group.results.map((result, resultIndex) => (
							<TableRow
								key={result.HeatMechanicalResultId}
								sx={rowSx(resultIndex === 0)}
							>
								<TableCell>{result.SampleSequence}</TableCell>
								{VALUE_COLUMNS.map((column) => {
									const failed = column.passed(result) === false;
									const detail = failed ? column.failDetail(result) : null;
									return (
										<TableCell
											key={column.field}
											sx={{
												color: failed ? "error.main" : undefined,
												fontWeight: failed ? 600 : undefined,
											}}
										>
											{detail ? (
												<Tooltip title={detail} arrow>
													<span>{column.format(result)}</span>
												</Tooltip>
											) : (
												column.format(result)
											)}
										</TableCell>
									);
								})}
								<TableCell align="center">
									<ResultChip passed={result.OverallPassed} />
								</TableCell>
								{resultIndex === 0 && renderRunCells(group)}
							</TableRow>
						));
					})}
				</TableBody>

				<TableFooter>
					<TableRow>
						<TableCell sx={refCellSx}>
							<Tooltip
								title={
									hasMixedLimits
										? "Requirement limits for this grade. Older runs were reported against a different requirement version"
										: "Requirement limits for this grade"
								}
								arrow
							>
								<span>{hasMixedLimits ? "Ref *" : "Ref"}</span>
							</Tooltip>
						</TableCell>
						{VALUE_COLUMNS.map((column) => (
							<TableCell key={column.field} sx={refCellSx}>
								{refResult ? column.range(refResult) : "-"}
							</TableCell>
						))}
						{[0, 1, 2, 3, 4, 5].map((index) => (
							<TableCell key={index} sx={refCellSx} />
						))}
					</TableRow>
				</TableFooter>
			</Table>
		</TableContainer>
	);
};
